import { useEffect } from "react";
import "@/App.css";
import { Wrench } from "lucide-react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { CriticalNoticeWatcher } from "@/components/CriticalNoticeWatcher";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clientes from "@/pages/Clientes";
import Cotizaciones from "@/pages/Cotizaciones";
import Ordenes from "@/pages/Ordenes";
import Facturas from "@/pages/Facturas";
import Cobranza from "@/pages/Cobranza";
import Conciliar from "@/pages/Conciliar";
import Remisiones from "@/pages/Remisiones";
import Productos from "@/pages/Productos";
import Configuracion from "@/pages/Configuracion";
import DocumentoPrint from "@/pages/DocumentoPrint";
import DocumentoDetalle from "@/pages/DocumentoDetalle";
import RemisionDetalle from "@/pages/RemisionDetalle";
import ReportePrint from "@/pages/ReportePrint";
import ReporteCobranzaPrint from "@/pages/ReporteCobranzaPrint";
import ReporteConciliacionPrint from "@/pages/ReporteConciliacionPrint";
import EstadoCuenta from "@/pages/EstadoCuenta";

const MODULE_ROUTE = {
  dashboard: "/", cotizaciones: "/cotizaciones", remisiones: "/remisiones",
  ordenes: "/ordenes", facturas: "/facturas", cobranza: "/cobranza",
  productos: "/productos", clientes: "/clientes", configuracion: "/configuracion",
};

function MaintenanceScreen() {
  const { logout } = useAuth();
  return (
    <div className="flex h-screen flex-col items-center justify-center bg-[#16202B] text-center px-6" data-testid="maintenance-screen">
      <div className="w-16 h-16 rounded-full border-2 border-[#E0A82E] flex items-center justify-center mb-6">
        <Wrench className="text-[#E0A82E]" size={30} />
      </div>
      <h1 className="text-white text-2xl sm:text-3xl font-semibold mb-3">Sistema en mantenimiento</h1>
      <p className="text-[#9AA7B4] font-mono-data text-sm max-w-md">
        Por favor intente más tarde. El sistema se encuentra temporalmente fuera de servicio por tareas de mantenimiento.
      </p>
      <button onClick={logout} data-testid="maintenance-logout"
        className="mt-8 font-mono-data text-xs uppercase tracking-wider text-[#9AA7B4] border border-[#3A4959] px-4 py-2 hover:bg-white/5 transition-colors">
        Cerrar sesión
      </button>
    </div>
  );
}

function Protected({ children, module }) {
  const { user, checked, can, maintenance, maintChecked } = useAuth();
  if (!checked)
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF2F6]">
        <div className="font-mono-data text-[#5B6672] animate-pulse">Cargando…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && !maintChecked)
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF2F6]">
        <div className="font-mono-data text-[#5B6672] animate-pulse">Cargando…</div>
      </div>
    );
  if (maintenance && user.role !== "admin") return <MaintenanceScreen />;
  if (module && !can(module)) {
    const first = (user.permissions || [])[0];
    return <Navigate to={MODULE_ROUTE[first] || "/"} replace />;
  }
  return <Layout>{children}</Layout>;
}

function ProtectedBare({ children }) {
  const { user, checked, maintenance, maintChecked } = useAuth();
  if (!checked)
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF2F6]">
        <div className="font-mono-data text-[#5B6672] animate-pulse">Cargando…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && !maintChecked)
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF2F6]">
        <div className="font-mono-data text-[#5B6672] animate-pulse">Cargando…</div>
      </div>
    );
  if (maintenance && user.role !== "admin") return <MaintenanceScreen />;
  return children;
}

function PublicOnly({ children }) {
  const { user, checked } = useAuth();
  if (!checked) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/" element={<Protected module="dashboard"><Dashboard /></Protected>} />
      <Route path="/clientes" element={<Protected module="clientes"><Clientes /></Protected>} />
      <Route path="/clientes/:id/estado" element={<Protected module="clientes"><EstadoCuenta /></Protected>} />
      <Route path="/cotizaciones" element={<Protected module="cotizaciones"><Cotizaciones /></Protected>} />
      <Route path="/remisiones" element={<Protected module="remisiones"><Remisiones /></Protected>} />
      <Route path="/ordenes" element={<Protected module="ordenes"><Ordenes /></Protected>} />
      <Route path="/facturas" element={<Protected module="facturas"><Facturas /></Protected>} />
      <Route path="/cobranza" element={<Protected module="cobranza"><Cobranza /></Protected>} />
      <Route path="/conciliar" element={<Protected module="conciliar"><Conciliar /></Protected>} />
      <Route path="/productos" element={<Protected module="productos"><Productos /></Protected>} />
      <Route path="/configuracion" element={<Protected module="configuracion"><Configuracion /></Protected>} />
      <Route path="/documento/remision/:id" element={<ProtectedBare><RemisionDetalle /></ProtectedBare>} />
      <Route path="/documento/:type/:id" element={<ProtectedBare><DocumentoDetalle /></ProtectedBare>} />
      <Route path="/imprimir/:type/:id" element={<DocumentoPrint />} />
      <Route path="/reporte/:type" element={<ReportePrint />} />
      <Route path="/reporte-cobranza" element={<ReporteCobranzaPrint />} />
      <Route path="/reporte-conciliacion" element={<ReporteConciliacionPrint />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = "TAS Publicidad 360 · ERP";
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <CriticalNoticeWatcher />
          <Toaster position="bottom-right" theme="light" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
